import { useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PUBLIC_PATH from "@/src/routes/public/path";
import { Controller, useForm } from "react-hook-form";
import { styled } from "styled-components";
import useAlert from "@/src/hooks/useAlert";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Typo from "@/src/components/atom/Typo";
import Checkbox from "@/src/components/atom/Checkbox";
import Input from "@/src/components/atom/Input";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import MultipleSelect from "@/src/components/atom/Select/MultipleSelect";
import TextArea from "@/src/components/atom/TextArea";
import { InputError } from "@/src/components/molecule/FormItem";
import { useJoinExistingCompanyMutation } from "@/src/store/apis/auth";
import { ClientType } from "@/src/store/apis/auth/interface";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { SUBSCRIPTION_PATH_OPTION_LIST } from "@/src/constant/optionList";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { useAppDispatch } from "@/src/store";
import { setUser } from "@/src/store/slice/auth";
import {
  MemberSignupStepContext,
  MemberSignUpResourceContext,
} from "./index";

const SEPARATOR = "&";
const SELECT_INLINE_STYLE = { width: "140px", flexShrink: 0 };

interface MemberInfoFormData {
  category: string[];
  officeContactPrefix?: string;
  officeContact?: string;
  joinPath?: string;
  joinPathDescription?: string;
  exporterUserMainFieldType: ClientType;
}

const MemberSignupStepTwo: React.FC = () => {
  const { t, i18n } = useTranslation([
    "signup",
    "signupStaff",
    "signupCorp",
    "common",
    "error",
  ]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const alert = useAlert();

  const stepContext = useContext(MemberSignupStepContext);
  const resourceContext = useContext(MemberSignUpResourceContext);

  const [joinExistingCompany, joinData] = useJoinExistingCompanyMutation();

  const {
    data: mainCategoryCodeList,
    isFetching: isMainCategoryCodeListFetching,
    isError: isMainCategoryCodeListError,
  } = useGetCommonCodeViaCodeNameQuery(
    { codeName: "MAIN_CATEGORY" },
    { refetchOnMountOrArgChange: true }
  );

  const { isCountryCodeFetching, countryCodeOptionList, countryCodeList } =
    useGetCommonCodeViaCodeNameQuery(
      {
        codeName: "COUNTRY",
      },
      {
        selectFromResult: ({ currentData, isError, isFetching }) => ({
          countryCodeOptionList: !isError
            ? (currentData ?? [])
                .filter((val) => val.value1 && val.codeItemName)
                .reduce<{ label: string; value: string }[]>(
                  (acc, val) => {
                    return [
                      ...acc,
                      {
                        label: `${val.value1} (${val.codeItemName})`,
                        value: [
                          val.codeItemName,
                          val.codeItemNameEn,
                          val.codeItemNameKo,
                          val.value1,
                        ].join(SEPARATOR),
                      },
                    ];
                  },
                  []
                )
            : [],
          isCountryCodeFetching: isFetching,
          countryCodeList: currentData ?? [],
        }),

        refetchOnMountOrArgChange: true,
      }
    );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<MemberInfoFormData>({
    defaultValues: {
      category: [],
      officeContactPrefix: "",
      officeContact: "",
      joinPath: undefined,
      joinPathDescription: "",
      exporterUserMainFieldType: "BOTH",
    },
  });

  const mainCategoryCodeListToSelectOption = useMemo(() => {
    if (isMainCategoryCodeListError || !mainCategoryCodeList) return [];

    const companyCategories =
      resourceContext?.mainCategoryCodeItemNames || [];

    return mainCategoryCodeList
      .filter((item) => companyCategories.includes(item.codeItemName))
      .map((item) => ({
        label:
          i18n.language === "ko" ? item.codeItemNameKo : item.codeItemNameEn,
        value: [item.codeItemName, item.codeItemNameEn, item.codeItemNameKo].join(
          SEPARATOR
        ),
      }));
  }, [
    mainCategoryCodeList,
    resourceContext?.mainCategoryCodeItemNames,
    isMainCategoryCodeListError,
    i18n.language,
  ]);

  const categoryValue = watch("category").map((codeName) => {
    const categoryItem = mainCategoryCodeList?.find(
      ({ codeItemName }) => codeName === codeItemName
    );
    return [
      categoryItem?.codeItemName,
      categoryItem?.codeItemNameEn,
      categoryItem?.codeItemNameKo,
    ].join(SEPARATOR);
  });

  const isAllCategorySelected =
    watch("category").length === mainCategoryCodeListToSelectOption?.length &&
    mainCategoryCodeListToSelectOption.length > 0;

  const handleSelectAll = () => {
    if (isAllCategorySelected) {
      setValue("category", []);
    } else {
      setValue(
        "category",
        mainCategoryCodeListToSelectOption?.map(
          ({ value }) => value.split(SEPARATOR)[0]
        ) ?? []
      );
    }
    clearErrors("category");
  };


  if (!stepContext || !resourceContext) {
    return null;
  }

  const { onStepPrev } = stepContext;
  const { companyName, exporterId } = resourceContext;

  const isSubmitting = joinData.isLoading;

  const handleBack = () => {
    onStepPrev();
  };

  const onSubmit = async (data: MemberInfoFormData) => {
    if (data.category.length === 0) {
      alert.showAlert({
        message: t("error:required"),
        type: "error",
      });
      return;
    }

    if (!exporterId) {
      alert.showAlert({
        message: t("error:unexpectedError"),
        type: "error",
      });
      return;
    }

    try {
      const exporterUserDto = await joinExistingCompany({
        exporterId,
        mainCategoryCodeItemNames: data.category,
        exporterUserMainFieldType: data.exporterUserMainFieldType,
      }).unwrap();

      dispatch(setUser(exporterUserDto));
      navigate(PUBLIC_PATH.SIGNUP_MEMBER_RESULT);
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      alert.showAlert({
        message: err.data?.message || t("error:unexpectedError"),
        type: "error",
      });
    }
  };

  return (
    <Container>
      <CompanyTitle>
        <Typo typoType="d3" color="gray1">
          <CompanyNameSpan typoType="d3" color="systemBlue2">
            {companyName}
          </CompanyNameSpan>{" "}
          {t("signup:v2.membersTitle")}
        </Typo>
      </CompanyTitle>

      <FormWrapper>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <CategoryFieldGroup>
            <FieldHeader>
              <FieldLabel>
                <Typo typoType="b7m" color="gray2">
                  {t("signup:v2.category")}
                </Typo>
                <Required>*</Required>
              </FieldLabel>
              <CheckboxContainer>
                <Checkbox
                  id="all-select"
                  checked={isAllCategorySelected}
                  onChange={handleSelectAll}
                  disabled={isMainCategoryCodeListFetching}
                />
                <CheckboxLabel htmlFor="all-select">
                  <Typo typoType="b9r" color="gray6">
                    {t("common:all")}
                  </Typo>
                </CheckboxLabel>
              </CheckboxContainer>
            </FieldHeader>

            {isMainCategoryCodeListFetching ? (
              <LoaderContainer>
                <Loader size={20} />
              </LoaderContainer>
            ) : (
              <Controller
                name="category"
                control={control}
                rules={{ required: t("error:required") }}
                render={({ field }) => (
                  <StyledCheckboxSelect
                    {...field}
                    data-invalid={!!errors.category}
                    placeholder={t(
                      "signup:v2.category.placeholder"
                    )}
                    disabled={
                      isMainCategoryCodeListFetching ||
                      isMainCategoryCodeListError
                    }
                    value={categoryValue}
                    onChange={(selectedItems) => {
                      const realValueList = selectedItems.map(
                        (item: string) => item.split(SEPARATOR)[0]
                      );
                      setValue("category", realValueList);
                      clearErrors("category");
                    }}
                    onRemoveItem={(item) => {
                      const codeNameValue = item.split(SEPARATOR)[0];
                      setValue(
                        "category",
                        watch("category").filter(
                          (codeName) => codeName !== codeNameValue
                        )
                      );
                    }}
                    options={mainCategoryCodeListToSelectOption}
                  />
                )}
              />
            )}
            {errors.category && <InputError message={errors.category.message} />}
          </CategoryFieldGroup>

          <FieldGroup>
            <FieldHeader>
              <FieldLabel>
                <Typo typoType="b7m" color="gray2">
                  {t("signup:v2.companyContact")}
                </Typo>
              </FieldLabel>
            </FieldHeader>

            <ContactRow>
              <Controller
                name="officeContactPrefix"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      {...field}
                      disabled={isCountryCodeFetching}
                      suffixIcon={isCountryCodeFetching ? <Loader /> : undefined}
                      showSearch
                      style={SELECT_INLINE_STYLE}
                      placeholder={t("placeholder:contactPrefix").replace(
                        "`",
                        ""
                      )}
                      options={countryCodeOptionList}
                    />
                  );
                }}
              />

              <Controller
                name="officeContact"
                control={control}
                render={({ field }) => {
                  return (
                    <ContactInput
                      {...field}
                      placeholder={t("signup:v2.contactCompany.placeholder")}
                      onBlur={(e) => {
                        field.onBlur();
                        const noZeroPrefix = e.target.value.replace(/^0+/, "");
                        setValue("officeContact", noZeroPrefix);
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        const onlyNumber = value.replace(/[^0-9]/g, "");
                        field.onChange(onlyNumber);
                      }}
                      onClear={() => setValue("officeContact", "")}
                    />
                  );
                }}
              />
            </ContactRow>
          </FieldGroup>

          <FieldGroup>
            <FieldHeader>
              <FieldLabel>
                <Typo typoType="b7m" color="gray2">
                  {t("signup:v2.signupPath")}
                </Typo>
              </FieldLabel>
            </FieldHeader>

            <Controller
              name="joinPath"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("signup:v2.signupPath.placeholder")}
                  options={SUBSCRIPTION_PATH_OPTION_LIST.map(
                    ({ langKey, value }) => {
                      return { label: t(langKey), value };
                    }
                  )}
                  allowClear={!!watch("joinPath")}
                />
              )}
            />
            {watch("joinPath") === "ETC" && (
              <Controller
                name="joinPathDescription"
                control={control}
                render={({ field }) => (
                  <StyledTextArea
                    {...field}
                    placeholder={t("common:joinPathDescriptionPlaceholder")}
                    maxLength={100}
                  />
                )}
              />
            )}
          </FieldGroup>

          <ButtonGroup>
            <BasicButton
              buttonGrade="tertiary"
              buttonColor="black"
              buttonSize={40}
              onClick={handleBack}
              disabled={isSubmitting}
              style={{
                width: "160px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typo typoType="btn3m" color="gray2">
                {t("common:back")}
              </Typo>
            </BasicButton>
            <BasicButton
              buttonGrade="primary"
              buttonColor="blue"
              buttonSize={40}
              type="submit"
              disabled={isSubmitting || watch("category").length === 0}
              style={{
                flex: 1,
                minHeight: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isSubmitting ? (
                <Loader size={20} />
              ) : (
                <Typo typoType="btn3m" color="white">
                  {t("signup:v2.createStaffAccount")}
                </Typo>
              )}
            </BasicButton>
          </ButtonGroup>
        </FormContainer>
      </FormWrapper>
    </Container>
  );
};

export default MemberSignupStepTwo;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
`;

const CompanyTitle = styled.div`
  text-align: center;
  padding: 24px 0;
  width: 100%;
  ${typo.d3}
  span {
    display: inline;
  }
`;

const CompanyNameSpan = styled(Typo)`
  display: inline;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 520px;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const CategoryFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Required = styled.span`
  color: ${colorSet.red2};
  ${typo.b7m}
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const StyledCheckboxSelect = styled(MultipleSelect)`
  &[data-invalid="true"] {
    .ant-select-selector {
      border: 1px solid ${colorSet.red2} !important;
    }
  }
`;

const ContactRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const ContactInput = styled(Input)`
  flex: 1;
`;

const StyledTextArea = styled(TextArea)`
  height: 137px;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
`;
